import { Injectable } from "@angular/core";
import { Store, select } from "@ngrx/store";
import { Observable } from "rxjs";

import {
	IntegrationsDTO,
	LoginPayloadParameters,
	ValidationSettingsDTO,
	LoginUserDTO,
	CustomerInfoDTO,
} from "@guest-web/api";
import {
	IAppCommonState,
	LoginUser,
	selectGuestValidationActive,
	selectGuestValidationIntegrations,
	selectGuestValidationSettings,
	selectLoginLoading,
	selectLoginUser,
	Logout,
	ClearLoyaltyProgram,
} from "@guest-web/core";
import { NavigationFacadeService } from "@guest-web-app/core/services/navigation-facade.service";

import {
	GUEST_DATA_KEY,
	GUEST_LOGGED_IN,
	SESSION_EXPIRY_KEY,
	STORAGE_EXPIRY_KEY,
} from "../@core/models/session-storage-keys";
import { CommonFacadeService } from "../@core/services/common-facade.service";
import { InitializationService } from "../@core/services/initialization.service";
import { LocalStorageService } from "../@core/services/local-storage.service";
import { SessionStorageService } from "../@core/services/session-storage.service";

@Injectable()
export class AuthenticationFacadeService {
	isGuestValidationActive$: Observable<boolean>;
	guestValidationSettings$: Observable<ValidationSettingsDTO>;
	guestValidationIntegrations$: Observable<IntegrationsDTO>;
	loginLoading$: Observable<boolean>;
	loginUser$: Observable<LoginUserDTO>;

	private readonly SESSION_DURATION = 3 * 60 * 60 * 1000; // 3 hours
	private readonly STORAGE_DURATION = 5 * 24 * 60 * 60 * 1000; // 5 days

	constructor(
		private store: Store<IAppCommonState>,
		private sessionStorage: SessionStorageService,
		private localStorage: LocalStorageService,
		private navigationFacade: NavigationFacadeService,
		private commonFacade: CommonFacadeService,
		private initializationFacade: InitializationService,
	) {
		this.isGuestValidationActive$ = this.store.pipe(select(selectGuestValidationActive));
		this.guestValidationSettings$ = this.store.pipe(select(selectGuestValidationSettings));
		this.guestValidationIntegrations$ = this.store.pipe(
			select(selectGuestValidationIntegrations),
		);
		this.loginLoading$ = this.store.pipe(select(selectLoginLoading));
		this.loginUser$ = this.store.pipe(select(selectLoginUser));
	}

	login(payload: LoginPayloadParameters): void {
		this.store.dispatch(new LoginUser(payload));
	}

	logout(returnUrl = ""): void {
		this.sessionStorage.removeSessionItem(GUEST_DATA_KEY);
		this.localStorage.removeStorageItem(GUEST_DATA_KEY);
		this.sessionStorage.removeSessionItem(SESSION_EXPIRY_KEY);
		this.localStorage.removeStorageItem(STORAGE_EXPIRY_KEY);
		this.sessionStorage.removeSessionItem(GUEST_LOGGED_IN);
		this.localStorage.removeStorageItem(GUEST_LOGGED_IN);
		this.initializationFacade.clearCustomerInfo();
		this.clearLoyaltyProgram();

		this.navigationFacade.go({
			path: [`${this.commonFacade.loginUrl}/login`],
			extras: { queryParams: { returnUrl } },
		});
	}

	clearLoginState(): void {
		this.store.dispatch(new Logout());
	}

	setUserSession(data, rememberMe: boolean): void {
		if (rememberMe) {
			this.localStorage.setStorageItem(GUEST_LOGGED_IN, true);
			this.localStorage.setStorageItem(GUEST_DATA_KEY, JSON.stringify(data));
			this.setAuthenticationStorageExpiry();
		} else {
			this.sessionStorage.setSessionItem(GUEST_LOGGED_IN, true);
			this.sessionStorage.setSessionItem(GUEST_DATA_KEY, JSON.stringify(data));
			this.setAuthenticationSessionExpiry();
		}
	}

	setAuthenticationSessionExpiry(): void {
		const expiryTime = new Date().getTime() + this.SESSION_DURATION;
		this.sessionStorage.setSessionItem(SESSION_EXPIRY_KEY, expiryTime.toString());
	}

	setAuthenticationStorageExpiry(): void {
		const expiryTime = new Date().getTime() + this.STORAGE_DURATION;
		this.localStorage.setStorageItem(STORAGE_EXPIRY_KEY, expiryTime.toString());
	}

	isAuthencationSessionActive(): boolean {
		const expiryTime =
			this.sessionStorage.getSessionItem(SESSION_EXPIRY_KEY) ||
			this.localStorage.getStorageItem(STORAGE_EXPIRY_KEY);
		if (!expiryTime) {
			return false;
		}

		const currentTime = new Date().getTime();
		return currentTime < +expiryTime;
	}

	updateUserData(data: CustomerInfoDTO): void {
		if (this.sessionStorage.getSessionItem(GUEST_DATA_KEY)) {
			this.sessionStorage.setSessionItem(GUEST_DATA_KEY, JSON.stringify(data));
		}
		if (this.localStorage.getStorageItem(GUEST_DATA_KEY)) {
			this.localStorage.setStorageItem(GUEST_DATA_KEY, JSON.stringify(data));
		}
	}

	isUserValidated(customerUUid = null): boolean {
		const isActive = this.isAuthencationSessionActive();
		const isUserValidated = this.getUserLoggedIn();
		const userData = this.getUserData();
		const customerUuid = customerUUid || this.getCustomerUuid();
		const isSameUser = userData?.uuid === customerUuid || false;

		return isActive && isUserValidated && isSameUser;
	}

	getUserLoggedIn(): boolean {
		return Boolean(
			this.sessionStorage.getSessionItem(GUEST_LOGGED_IN) ||
			this.localStorage.getStorageItem(GUEST_LOGGED_IN),
		);
	}

	getUserData(): LoginUserDTO {
		return JSON.parse(
			this.sessionStorage.getSessionItem(GUEST_DATA_KEY) ||
				this.localStorage.getStorageItem(GUEST_DATA_KEY),
		);
	}

	private getCustomerUuid(): string {
		let constactUuid = "";
		const fullPath = window.location.pathname;
		const pathArray = fullPath.split("/");
		const uUidsArray = [];
		const uuidRegEx = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/gi;

		for (const value of pathArray) {
			uuidRegEx.lastIndex = 0; // regexp keeps state when using 'g'
			if (uuidRegEx.test(value)) {
				uUidsArray.push(value);
			}
		}

		if (uUidsArray.length > 1) {
			const value = uUidsArray[1];
			uuidRegEx.lastIndex = 0; // regexp keeps state when using 'g'
			if (uuidRegEx.test(value)) {
				constactUuid = value;
			}
		}

		return constactUuid;
	}

	private clearLoyaltyProgram(): void {
		this.store.dispatch(new ClearLoyaltyProgram());
	}
}
